<script setup>
import { reactive, computed, ref, watch, getCurrentInstance, onMounted } from 'vue'
import { useForm, router } from '@inertiajs/vue3';
import { useCurrencyFormatter } from '@/utils/currencyFormatter.js';
import { watchDebounced } from '@vueuse/core';
import AdminMainLayout from '@/layout/admin/AdminMainLayout.vue';
import { useLoading } from '@/composables/useLoading';
import { useEnhancedLoading } from '@/composables/useEnhancedLoading';
import { useOrderStatus } from '@/composables/useOrderStatus.js';
import OrderStepProgress from '@/components/orders/OrderStepProgress.vue';
import { useToast } from 'primevue/usetoast';
import OrderDetailsDialog from '@/components/admin/orders/OrderDetailsDialog.vue';
import axios from 'axios';

defineOptions({
    layout: AdminMainLayout,
});

const { proxy } = getCurrentInstance();
const $t = proxy.$t;
const { showLoading, hideLoading } = useLoading();
const { showPageLoading, hidePageLoading } = useEnhancedLoading('admin-orders-table');
const { getStatusLabel, getStatusSeverity, getCancellationTooltip, canCancelOrder } = useOrderStatus();
const toast = useToast();

// Cancel order state
const showCancelModal = ref(false);
const showCancelConfirmModal = ref(false);
const cancelReason = ref('');
const cancellingOrder = ref(false);
const selectedOrderToCancel = ref(null);

// Payment modal state
const showPaymentModal = ref(false);
const paymentModalOrder = ref(null);
const paymentItems = ref([]);
const togglingItemId = ref(null);
const paymentModalDirty = ref(false);

const openPaymentModal = (order) => {
    paymentModalOrder.value = order;
    paymentItems.value = (order.items_for_payment || []).map(item => ({ ...item }));
    paymentModalDirty.value = false;
    showPaymentModal.value = true;
};

const closePaymentModal = () => {
    showPaymentModal.value = false;
    if (paymentModalDirty.value) {
        router.reload({ only: ['orders'] });
    }
    paymentModalOrder.value = null;
    paymentItems.value = [];
};

const toggleItemPaid = async (item) => {
    togglingItemId.value = item.id;
    try {
        const response = await axios.patch(`/admin/order-items/${item.id}/paid`);
        item.paid = response.data.paid;
        paymentModalDirty.value = true;
    } catch {
        toast.add({ severity: 'error', summary: $t('Error'), detail: $t('Failed to update payment status'), life: 3000 });
    } finally {
        togglingItemId.value = null;
    }
};

// Details modal state
const showDetailsModal = ref(false);
const detailsOrderId = ref(null);

// Mock coinStore para evitar erro do Pinia
const coinStore = reactive({
    coin: 'USD',
    ratios: {
        'BRL': 1,
        'EUR': 1,
        'USD': 1,
        'GBP': 1
    }
})

const props = defineProps({
    orders: Object,
    statuses: Object,
    mainStatuses: Object,
    statusCounts: Object,
    totalCount: Number,
    clients: Array,
    sellers: Array,
    importFailures: Object,
    importDiff: Number,
    sites: Object,
    coins: Object,
    filters: Object,
});

const links = computed(() => {
    const _links = props.orders.links;
    _links.shift();
    _links.pop();
    return _links;
});

const filters = reactive({
    search: props.filters.search,
    client: props.filters.client,
    status: props.filters.status,
    created_at: props.filters.created_at,
});

watch(
    filters,
    () => {
        showPageLoading();
    },
    { deep: true }
);

watchDebounced(
    filters,
    (n, o) => get(),
    {
        debounce: 400,
        deep: true,
    }
);

const get = async () => {
    router.get(
        proxy.route ? proxy.route('orders.index') : '/admin/orders',
        {
            filter: filters,
        },
        {
            preserveState: true,
            preserveScroll: true,
            only: ['orders', 'filters'],
            onStart: () => {
                showLoading($t('Loading orders...'));
            },
            onFinish: () => {
                hideLoading();
                hidePageLoading();
            },
            onError: () => {
                hideLoading();
                hidePageLoading();
            }
        }
    );
};

const filterByStatus = (status) => {
    if (filters.status === status) {
        filters.status = null;
    } else {
        filters.status = status;
    }
    showPageLoading();
};

const filterByConfirmOrder = () => {
    if (filters.status === 'CONFIRM_ORDER') {
        filters.status = null;
    } else {
        filters.status = 'CONFIRM_ORDER';
    }
    showPageLoading();
};

const filterByConfirmPublication = () => {
    if (filters.status === 'CONFIRM_PUBLICATION') {
        filters.status = null;
    } else {
        filters.status = 'CONFIRM_PUBLICATION';
    }
    showPageLoading();
};

const filterByGenerateInvoice = () => {
    if (filters.status === 'GENERATE_INVOICE') {
        filters.status = null;
    } else {
        filters.status = 'GENERATE_INVOICE';
    }
    showPageLoading();
};

const filterByConfirmDeferredPayment = () => {
    if (filters.status === 'CONFIRM_DEFERRED_PAYMENT') {
        filters.status = null;
    } else {
        filters.status = 'CONFIRM_DEFERRED_PAYMENT';
    }
    showPageLoading();
};

const filterByCompleted = () => {
    if (filters.status === 'COMPLETED') {
        filters.status = null;
    } else {
        filters.status = 'COMPLETED';
    }
    showPageLoading();
};

const filterByAwaitingClientApproval = () => {
    if (filters.status === 'AWAITING_CLIENT_APPROVAL') {
        filters.status = null;
    } else {
        filters.status = 'AWAITING_CLIENT_APPROVAL';
    }
    showPageLoading();
};

// Computed para verificar quais filtros estão ativos
const isConfirmOrderActive = computed(() => filters.status === 'CONFIRM_ORDER');
const isConfirmPublicationActive = computed(() => filters.status === 'CONFIRM_PUBLICATION');
const isGenerateInvoiceActive = computed(() => filters.status === 'GENERATE_INVOICE');
const isConfirmDeferredPaymentActive = computed(() => filters.status === 'CONFIRM_DEFERRED_PAYMENT');
const isCompletedActive = computed(() => filters.status === 'COMPLETED');
const isAwaitingClientApprovalActive = computed(() => filters.status === 'AWAITING_CLIENT_APPROVAL');

// Computed para contar cada tipo
const confirmOrderCount = computed(() => {
    return (props.statusCounts?.WAITING || 0) +
           (props.statusCounts?.PRODUCTION || 0) +
           (props.statusCounts?.SUBMITTED || 0);
});

const confirmPublicationCount = computed(() => {
    return props.statusCounts?.PROCESSING || 0;
});

const generateInvoiceCount = computed(() => {
    return props.statusCounts?.GENERATE_INVOICE || 0;
});

const confirmDeferredPaymentCount = computed(() => {
    return props.statusCounts?.CONFIRM_DEFERRED_PAYMENT || 0;
});

const awaitingClientApprovalCount = computed(() => {
    return props.statusCounts?.AWAITING_CLIENT_APPROVAL || 0;
});

const completedCount = computed(() => {
    return props.statusCounts?.COMPLETED || 0;
});



const clearStatusFilter = () => {
    filters.status = null;
    showPageLoading();
};

const hasActiveFilters = () => {
    return filters.search ||
           filters.client ||
           filters.status;
};



const importFilepond = ref(null);
const openImportDialog = ref(false);
const importFinished = ref(false);

const uploadOrders = (fieldName, file, metadata, load, error, progress, abort) => {
    router.post(
        proxy.route ? proxy.route('orders.import') : '/admin/orders/import',
        {
            file: file,
        },
        {
            forceFormData: true,
            preserveScroll: true,
            preserveState: true,
            onStart: () => showLoading($t('Importing orders...')),
            onFinish: () => hideLoading(),
            onError(err) {
                abort();
            },
            onSuccess(res) {
                load();
            },
            onProgress(p) {
                progress(true, p.loaded, p.total);
            },
            onFinish() {
                importFinished.value = true;
                hideLoading();
            },
        }
    );
};

watch(openImportDialog, (n, o) => {
    if (!n) {
        setTimeout(() => {
            importFinished.value = false;
            router.reload({
                only: ["importFailures", "importDiff"],
            });
        }, 300);
    }
});

const form = useForm({
    list: '',
    type: null,
});

const list = ref([]);

const openCancelModal = (order) => {
    selectedOrderToCancel.value = order;
    cancelReason.value = '';
    showCancelModal.value = true;
};

const closeCancelModal = () => {
    showCancelModal.value = false;
    if (!showCancelConfirmModal.value) {
        selectedOrderToCancel.value = null;
        cancelReason.value = '';
    }
};

const openCancelConfirmModal = () => {
    const currentReason = cancelReason.value;
    const currentOrder = selectedOrderToCancel.value;

    showCancelModal.value = false;

    setTimeout(() => {
        cancelReason.value = currentReason;
        selectedOrderToCancel.value = currentOrder;
        showCancelConfirmModal.value = true;
    }, 50);
};

const closeCancelConfirmModal = () => {
    showCancelConfirmModal.value = false;
    selectedOrderToCancel.value = null;
    cancelReason.value = '';
};

const confirmCancelOrder = async () => {
    if (!selectedOrderToCancel.value) {
        return;
    }

    cancellingOrder.value = true;

    try {
        const response = await axios.put(`/admin/orders/${selectedOrderToCancel.value.id}/cancel`, {
            cancellation_reason: cancelReason.value.trim() || null
        });

        toast.add({
            severity: 'success',
            summary: $t('Success'),
            detail: $t('Order canceled successfully. The client has been notified.'),
            life: 5000
        });

        closeCancelConfirmModal();

        get();

    } catch (error) {
        toast.add({
            severity: 'error',
            summary: $t('Error'),
            detail: error.response?.data?.message || $t('Failed to cancel order'),
            life: 5000
        });
    } finally {
        cancellingOrder.value = false;
    }
};

const openDetailsModal = (order) => {
    detailsOrderId.value = order.id;
    showDetailsModal.value = true;
};


const submit = () => {
    form.post(proxy.route ? proxy.route('orders.go') : '/admin/orders/go', {
        preserveScroll: true,
        onStart: () => showLoading($t('Processing order...')),
        onFinish: () => hideLoading(),
        onSuccess: (res) => {
            router.reload({
                only: ["sites"],
            });
            openOrderDialog.value = false;
            openSitesDialog.value = true;
            list.value = res.props.sites;
            form.reset();
        }
    });
};

const openOrderDialog = ref(false);
const openSitesDialog = ref(false);


const statusOptions = computed(() => [
    { label: $t('View All'), value: null },
    { label: $t('admin.statuses.waiting'), value: 'WAITING' },
    { label: $t('admin.statuses.production'), value: 'PRODUCTION' },
    { label: $t('Awaiting Client Approval'), value: 'AWAITING_CLIENT_APPROVAL' },
    { label: $t('admin.statuses.submitted'), value: 'SUBMITTED' },
    { label: $t('admin.statuses.processing'), value: 'PROCESSING' },
    { label: $t('admin.statuses.published'), value: 'PUBLISHED' },
    { label: $t('admin.statuses.invoice'), value: 'INVOICE' },
    { label: $t('admin.statuses.completed'), value: 'COMPLETED' },
    { label: $t('admin.statuses.canceled'), value: 'CANCELED' },
]);

const { formatWithSymbol } = useCurrencyFormatter();

const formatCurrency = (value, currency) => {
    if (!value || !currency) return '-';
    return formatWithSymbol(parseFloat(value) || 0, currency);
};

onMounted(() => {
    setTimeout(() => {
        if (window.tippy) {
            window.tippy('[data-tippy-content]');
        }
    }, 500);
});
</script>

<template>
    <!-- Import Dialog -->
    <Dialog v-model:visible="openImportDialog" modal :header="$t('Import orders')" :style="{ width: '90vw', maxWidth: '32rem' }" :breakpoints="{ '960px': '75vw', '641px': '100vw' }">
        <div class="flex flex-col gap-4">
            <FileUpload
                ref="importFilepond"
                mode="basic"
                name="file"
                accept=".csv"
                :maxFileSize="1000000"
                :auto="true"
                :choose-label="$t('Choose CSV File')"
                @upload="uploadOrders"
            />

            <div v-if="importFinished" class="text-center">
                <div class="mb-4">
                    <span class="block text-lg font-medium">{{ importDiff }} {{ $t('orders added') }}.</span>
                </div>

                <div v-if="importFailures && importFailures.length > 0" class="mt-4">
                    <DataTable :value="importFailures" class="text-sm">
                        <Column field="row" :header="$t('Row')" />
                        <Column field="attribute" :header="$t('Column')" />
                        <Column :header="$t('Value')">
                            <template #body="slotProps">
                                {{ slotProps.data.values[slotProps.data.attribute] || "-" }}
                            </template>
                        </Column>
                        <Column :header="$t('Error')">
                            <template #body="slotProps">
                                {{ slotProps.data.errors[0] }}
                            </template>
                        </Column>
                    </DataTable>
                </div>
            </div>
        </div>
    </Dialog>

    <!-- Quick Order Dialog -->
    <Dialog v-model:visible="openOrderDialog" modal :header="$t('Quick order')" :style="{ width: '90vw', maxWidth: '32rem' }" :breakpoints="{ '960px': '75vw', '641px': '100vw' }">
        <div class="flex flex-col gap-4">
            <div>
                <label class="block text-sm font-medium text-surface-700 mb-2">
                    {{ $t('Site list') }}
                </label>
                <Textarea
                    v-model="form.list"
                    rows="5"
                    class="w-full"
                    :placeholder="$t('Enter site URLs, one per line')"
                />
                <small v-if="form.errors.list" class="text-red-600">{{ form.errors.list }}</small>
            </div>
        </div>

        <template #footer>
            <Button
                :label="$t('Cancel')"
                severity="secondary"
                @click="openOrderDialog = false"
            />
            <Button
                :label="$t('Go')"
                @click="submit"
                :loading="form.processing"
                :disabled="form.processing"
            />
        </template>
    </Dialog>

    <!-- Sites Dialog -->
    <Dialog v-model:visible="openSitesDialog" modal :header="$t('Quick order')" :style="{ width: '90vw', maxWidth: '80rem' }" :breakpoints="{ '960px': '75vw', '641px': '100vw' }">
        <div class="flex flex-col gap-4">
            <DataTable v-if="list" :value="Object.entries(list)" class="w-full add-sites-table" responsiveLayout="scroll">
                <Column :header="$t('Portal')" style="min-width: 280px">
                    <template #body="slotProps">
                        <span class="break-all">{{ slotProps.data[0] }}</span>
                    </template>
                </Column>
                <Column :header="$t('Seller')">
                    <template #body="slotProps">
                        {{ slotProps.data[1]?.seller?.name || '-' }}
                    </template>
                </Column>
                <Column :header="$t('Purchase value')">
                    <template #body="slotProps">
                        <span v-if="slotProps.data[1] === null">-</span>
                        <span v-else class="flex items-center gap-2">
                            <span
                                v-if="slotProps.data[1].cost_coin !== coinStore.coin"
                                class="inline-block w-2 h-2 bg-primary-500 rounded-full"
                            />
                            <span>
                                {{ slotProps.data[1].cost_coin !== coinStore.coin ? '~ ' : '' }}
                                {{ formatCurrency(Math.ceil((slotProps.data[1].cost / coinStore.ratios[slotProps.data[1].cost_coin]) / 100), coinStore.coin) }}
                            </span>
                        </span>
                    </template>
                </Column>
                <Column :header="$t('Sale value')">
                    <template #body="slotProps">
                        <span v-if="slotProps.data[1] === null">-</span>
                        <span v-else class="flex items-center gap-2">
                            <span
                                v-if="slotProps.data[1].sale_coin !== coinStore.coin"
                                class="inline-block w-2 h-2 bg-primary-500 rounded-full"
                            />
                            <span>
                                {{ slotProps.data[1].sale_coin !== coinStore.coin ? '~ ' : '' }}
                                {{ formatCurrency(Math.ceil((slotProps.data[1].sale / coinStore.ratios[slotProps.data[1].sale_coin]) / 100), coinStore.coin) }}
                            </span>
                        </span>
                    </template>
                </Column>
            </DataTable>
        </div>

        <template #footer>
            <Button
                :label="$t('Copy')"
                severity="secondary"
                icon="pi pi-copy"
            />
            <Button
                :label="$t('Add order')"
                icon="pi pi-plus"
            />
        </template>
    </Dialog>

    <!-- Main Content -->
    <div class="grid">
        <!-- Search Filters Card -->
        <Card class="mb-6">
            <template #header>
                <div class="flex items-center justify-between p-4 pb-0">
                    <div class="flex items-center gap-3">
                        <i class="pi pi-search text-2xl text-surface-500" />
                        <div>
                            <h2 class="text-xl font-bold">{{ $t('Search orders') }}</h2>
                            <span class="text-sm text-surface-500">{{ $t('Choose filters to start') }}</span>
                        </div>
                    </div>
                </div>
            </template>

            <template #content>
                <div>
                    <div class="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-4 mb-6">
                        <div
                            @click="filterByConfirmOrder"
                            :class="[
                                'rounded-lg p-4 cursor-pointer transition-all',
                                isConfirmOrderActive
                                    ? 'bg-blue-100 border-2 border-blue-500 shadow-md'
                                    : 'bg-white border border-surface-300 hover:border-blue-400'
                            ]"
                        >
                            <div :class="[
                                'text-2xl font-bold mb-1',
                                isConfirmOrderActive ? 'text-blue-700' : 'text-surface-700'
                            ]">{{ confirmOrderCount }}</div>
                            <div :class="[
                                'text-sm',
                                isConfirmOrderActive ? 'text-blue-600' : 'text-surface-600'
                            ]">{{ $t('Confirm Order') }}</div>
                        </div>
                        <div
                            @click="filterByConfirmPublication"
                            :class="[
                                'rounded-lg p-4 cursor-pointer transition-all',
                                isConfirmPublicationActive
                                    ? 'bg-blue-100 border-2 border-blue-500 shadow-md'
                                    : 'bg-white border border-surface-300 hover:border-blue-400'
                            ]"
                        >
                            <div :class="[
                                'text-2xl font-bold mb-1',
                                isConfirmPublicationActive ? 'text-blue-700' : 'text-surface-700'
                            ]">{{ confirmPublicationCount }}</div>
                            <div :class="[
                                'text-sm',
                                isConfirmPublicationActive ? 'text-blue-600' : 'text-surface-600'
                            ]">{{ $t('Confirm Publication') }}</div>
                        </div>
                        <div
                            @click="filterByGenerateInvoice"
                            :class="[
                                'rounded-lg p-4 cursor-pointer transition-all',
                                isGenerateInvoiceActive
                                    ? 'bg-blue-100 border-2 border-blue-500 shadow-md'
                                    : 'bg-white border border-surface-300 hover:border-blue-400'
                            ]"
                        >
                            <div :class="[
                                'text-2xl font-bold mb-1',
                                isGenerateInvoiceActive ? 'text-blue-700' : 'text-surface-700'
                            ]">{{ generateInvoiceCount }}</div>
                            <div :class="[
                                'text-sm',
                                isGenerateInvoiceActive ? 'text-blue-600' : 'text-surface-600'
                            ]">{{ $t('Invoice') }}</div>
                        </div>
                        <div
                            @click="filterByConfirmDeferredPayment"
                            :class="[
                                'rounded-lg p-4 cursor-pointer transition-all',
                                isConfirmDeferredPaymentActive
                                    ? 'bg-blue-100 border-2 border-blue-500 shadow-md'
                                    : 'bg-white border border-surface-300 hover:border-blue-400'
                            ]"
                        >
                            <div :class="[
                                'text-2xl font-bold mb-1',
                                isConfirmDeferredPaymentActive ? 'text-blue-700' : 'text-surface-700'
                            ]">{{ confirmDeferredPaymentCount }}</div>
                            <div :class="[
                                'text-sm',
                                isConfirmDeferredPaymentActive ? 'text-blue-600' : 'text-surface-600'
                            ]">{{ $t('Confirm Deferred Payment') }}</div>
                        </div>
                        <div
                            @click="filterByAwaitingClientApproval"
                            :class="[
                                'rounded-lg p-4 cursor-pointer transition-all',
                                isAwaitingClientApprovalActive
                                    ? 'bg-blue-100 border-2 border-blue-500 shadow-md'
                                    : 'bg-white border border-surface-300 hover:border-blue-400'
                            ]"
                        >
                            <div :class="[
                                'text-2xl font-bold mb-1',
                                isAwaitingClientApprovalActive ? 'text-blue-700' : 'text-surface-700'
                            ]">{{ awaitingClientApprovalCount }}</div>
                            <div :class="[
                                'text-sm',
                                isAwaitingClientApprovalActive ? 'text-blue-600' : 'text-surface-600'
                            ]">{{ $t('Awaiting Client Approval') }}</div>
                        </div>
                        <div
                            @click="filterByCompleted"
                            :class="[
                                'rounded-lg p-4 cursor-pointer transition-all',
                                isCompletedActive
                                    ? 'bg-blue-100 border-2 border-blue-500 shadow-md'
                                    : 'bg-white border border-surface-300 hover:border-blue-400'
                            ]"
                        >
                            <div :class="[
                                'text-2xl font-bold mb-1',
                                isCompletedActive ? 'text-blue-700' : 'text-surface-700'
                            ]">{{ completedCount }}</div>
                            <div :class="[
                                'text-sm',
                                isCompletedActive ? 'text-blue-600' : 'text-surface-600'
                            ]">{{ $t('Completed') }}</div>
                        </div>
                    </div>
                </div>
                    <div class="space-y-4">
                        <!-- Mobile: Stacked layout -->
                        <div class="block lg:hidden space-y-4">
                        <!-- Search by number -->
                        <div>
                            <label class="block text-sm font-medium text-surface-700 mb-2">
                                {{ $t('Find by number:') }}
                            </label>
                            <IconField>
                                <InputIcon class="pi pi-search" />
                                <InputText
                                    v-model="filters.search"
                                    :placeholder="$t('Search')"
                                    class="w-full"
                                />
                            </IconField>
                        </div>

                        <!-- Filter by client -->
                        <div>
                            <label class="block text-sm font-medium text-surface-700 mb-2">
                                {{ $t('Filter by client') }}
                            </label>
                            <Select
                                v-model="filters.client"
                                :options="clients.map(client => ({ name: client.name, value: client.id }))"
                                optionLabel="name"
                                optionValue="value"
                                :placeholder="$t('Select...')"
                                class="w-full custom-select"
                                showClear
                            />
                        </div>

                        <!-- Filter by status -->
                        <div>
                            <label class="block text-sm font-medium text-surface-700 mb-2">
                                {{ $t('Filter by status') }}
                            </label>
                            <Select
                                v-model="filters.status"
                                :options="statusOptions"
                                optionLabel="label"
                                optionValue="value"
                                :placeholder="$t('Select...')"
                                class="w-full custom-select"
                            />
                        </div>

                        <!-- Date filter -->
                        <!-- <div>
                            <label class="block text-sm font-medium text-surface-700 mb-2">
                                {{ $t('Date') }}
                            </label>
                            <Calendar
                                v-model="filters.created_at"
                                selectionMode="range"
                                :manualInput="false"
                                showIcon
                                class="w-full"
                                dateFormat="dd/mm/yy"
                            />
                        </div> -->
                    </div>

                    <!-- Desktop: Grid layout -->
                    <div class="hidden lg:grid lg:grid-cols-4 gap-4">
                        <!-- Search by number -->
                        <div>
                            <label class="block text-sm font-medium text-surface-700 mb-2">
                                {{ $t('Find by number:') }}
                            </label>
                            <IconField>
                                <InputIcon class="pi pi-search" />
                                <InputText
                                    v-model="filters.search"
                                    :placeholder="$t('Search')"
                                    class="w-full"
                                />
                            </IconField>
                        </div>

                        <!-- Filter by client -->
                        <div>
                            <label class="block text-sm font-medium text-surface-700 mb-2">
                                {{ $t('Filter by client') }}
                            </label>
                            <Select
                                v-model="filters.client"
                                :options="clients.map(client => ({
                                    name: client.first_name && client.last_name
                                        ? `${client.first_name} ${client.last_name}`
                                        : client.name,
                                    value: client.id
                                }))"
                                optionLabel="name"
                                optionValue="value"
                                :placeholder="$t('Select...')"
                                class="w-full custom-select"
                                showClear
                            />
                        </div>

                        <!-- Filter by status -->
                        <div>
                            <label class="block text-sm font-medium text-surface-700 mb-2">
                                {{ $t('Filter by status') }}
                            </label>
                            <Select
                                v-model="filters.status"
                                :options="statusOptions"
                                optionLabel="label"
                                optionValue="value"
                                :placeholder="$t('Select...')"
                                class="w-full custom-select"
                            />
                        </div>

                        <!-- Date filter -->
                        <!-- <div>
                            <label class="block text-sm font-medium text-surface-700 mb-2">
                                {{ $t('Date') }}
                            </label>
                            <Calendar
                                v-model="filters.created_at"
                                selectionMode="range"
                                :manualInput="false"
                                showIcon
                                class="w-full"
                                dateFormat="dd/mm/yy"
                            />
                        </div> -->
                    </div>
                </div>
            </template>
        </Card>

        <!-- Results Section -->
        <div class="flex flex-col">
            <div class="flex flex-col lg:flex-row lg:justify-between lg:items-center mb-4 gap-4">
                <div class="flex-1">
                    <h2 class="text-lg lg:text-xl font-semibold text-surface-800">
                        {{ $t('Showing results:') }} {{ orders.total }} {{ $t('orders found') }}
                    </h2>
                </div>

                <!-- Mobile: Stacked buttons -->
                <div class="flex flex-col sm:flex-row gap-2 lg:hidden">
                    <Button
                        :label="$t('Add order')"
                        icon="pi pi-plus"
                        @click="() => {
                            try {
                                const routeName = proxy.route ? proxy.route('orders.create') : '/admin/orders/create';
                                router.visit(routeName, {
                                    onStart: () => showLoading($t('Loading...')),
                                    onFinish: () => hideLoading(),
                                    onError: () => hideLoading()
                                });
                            } catch (e) { console.error(e); hideLoading(); }
                        }"
                        class="w-full sm:w-auto text-sm"
                        size="small"
                    />
                </div>

                <!-- Desktop: Horizontal buttons -->
                <div class="hidden lg:flex gap-2">
                    <Button
                        :label="$t('Add order')"
                        icon="pi pi-plus"
                        @click="() => {
                            try {
                                const routeName = proxy.route ? proxy.route('orders.create') : '/admin/orders/create';
                                router.visit(routeName, {
                                    onStart: () => showLoading($t('Loading...')),
                                    onFinish: () => hideLoading(),
                                    onError: () => hideLoading()
                                });
                            } catch (e) { console.error(e); hideLoading(); }
                        }"
                    />
                </div>
            </div>

            <!-- Mobile: Card View -->
            <div class="block lg:hidden">
                <div v-if="!orders.data || orders.data.length === 0" class="text-center py-12">
                    <div class="mb-4">
                        <i class="pi pi-inbox text-4xl text-surface-300"></i>
                    </div>
                    <h4 class="text-lg font-semibold text-surface-600 mb-2">
                        {{ hasActiveFilters() ? $t('No results with the current filters') : $t('No orders found') }}
                    </h4>
                </div>

                <div class="space-y-3" v-else>
                    <Card v-for="order in orders.data" :key="order.id" class="p-4">
                        <template #content>
                            <div class="flex justify-between items-start mb-3">
                                <div class="flex items-center gap-2">
                                    <div>
                                        <h3 class="font-medium text-surface-900">#{{ order.number }}</h3>
                                        <p class="text-sm text-surface-600">{{ order.client?.name || "-" }}</p>
                                    </div>
                                </div>
                                <div class="flex items-center gap-2">
                                    <Tag
                                        :value="getStatusLabel(order.status, $t)"
                                        :severity="getStatusSeverity(order.status)"
                                        class="text-xs"
                                        v-tooltip.top="getCancellationTooltip(order, $t)"
                                    />
                                    <Button
                                        icon="pi pi-dollar"
                                        text
                                        rounded
                                        size="small"
                                        :severity="order.has_unpaid_required ? 'danger' : order.all_paid ? 'success' : 'info'"
                                        v-tooltip.top="order.has_unpaid_required ? $t('Has items requiring payment — mark as paid before publishing') : order.all_paid ? $t('All items paid') : $t('Has unpaid items')"
                                        :disabled="order.status === 'CANCELED'"
                                        @click="openPaymentModal(order)"
                                    />
                                    <Button
                                        icon="pi pi-eye"
                                        text
                                        rounded
                                        size="small"
                                        v-tooltip.top="$t('Details')"
                                        @click="() => openDetailsModal(order)"
                                    />
                                    <Button
                                        icon="pi pi-pencil"
                                        text
                                        rounded
                                        size="small"
                                        :disabled="order.status === 'CANCELED'"
                                        @click="() => {
                                            try {
                                                const routeName = proxy.route ? proxy.route('orders.edit', order.id) : `/admin/orders/${order.id}/edit`;
                                                router.visit(routeName, {
                                                    onStart: () => showLoading($t('Loading...')),
                                                    onFinish: () => hideLoading(),
                                                    onError: () => hideLoading()
                                                });
                                            } catch (e) { console.error(e); hideLoading(); }
                                        }"
                                    />
                                    <span v-tooltip.top="order.status === 'CANCELED' ? getCancellationTooltip(order, $t) : $t('Cancel Order')">
                                        <Button
                                            icon="pi pi-times-circle"
                                            text
                                            rounded
                                            size="small"
                                            :severity="order.status === 'CANCELED' ? 'secondary' : 'danger'"
                                            :disabled="!canCancelOrder(order)"
                                            @click="() => openCancelModal(order)"
                                            :class="order.status === 'CANCELED' ? 'opacity-50' : ''"
                                        />
                                    </span>
                                </div>
                            </div>
                            <div class="mb-3">
                                <OrderStepProgress :order="order" :stepIcons="order.stepIcons || {}" />
                            </div>
                            <div class="grid grid-cols-2 gap-2 text-sm">
                                <div>
                                    <span class="font-medium text-surface-700">{{ $t('Invoice') }}:</span>
                                    <Button
                                        v-if="order.invoice"
                                        :label="`#${order.invoice.number}`"
                                        link
                                        size="small"
                                        class="p-0 h-auto text-sm"
                                        @click="() => {
                                            try {
                                                const routeName = proxy.route ? proxy.route('invoices.edit', order.invoice.id) : `/admin/invoices/${order.invoice.id}/edit`;
                                                router.visit(routeName, {
                                                    onStart: () => showLoading($t('Loading...')),
                                                    onFinish: () => hideLoading(),
                                                    onError: () => hideLoading()
                                                });
                                            } catch (e) { console.error(e); hideLoading(); }
                                        }"
                                    />
                                    <span v-else class="text-surface-500">-</span>
                                </div>
                                <div>
                                    <span class="font-medium text-surface-700">{{ $t('Created at') }}:</span>
                                    <span class="text-surface-600">{{ new Date(order.created_at).toLocaleDateString() }}</span>
                                </div>
                            </div>
                        </template>
                    </Card>
                </div>

                <!-- Mobile Pagination -->
                <div class="flex justify-between items-center mt-4 gap-2">
                    <Button
                        :label="$t('Previous')"
                        icon="pi pi-chevron-left"
                        severity="secondary"
                        size="small"
                        :disabled="!orders.prev_page_url"
                        @click="() => {
                            if (orders.prev_page_url) {
                                router.visit(orders.prev_page_url, {
                                    onStart: () => showLoading($t('Loading previous page...')),
                                    onFinish: () => hideLoading(),
                                    onError: () => hideLoading()
                                });
                            }
                        }"
                    />
                    <span class="text-sm text-surface-600">
                        {{ ((orders.current_page || 1) - 1) * (orders.per_page || 15) + 1 }} -
                        {{ Math.min((orders.current_page || 1) * (orders.per_page || 15), orders.total || 0) }}
                        {{ $t('of') }} {{ orders.total || 0 }}
                    </span>
                    <Button
                        :label="$t('Next')"
                        icon="pi pi-chevron-right"
                        iconPos="right"
                        severity="secondary"
                        size="small"
                        :disabled="!orders.next_page_url"
                        @click="() => {
                            if (orders.next_page_url) {
                                router.visit(orders.next_page_url, {
                                    onStart: () => showLoading($t('Loading next page...')),
                                    onFinish: () => hideLoading(),
                                    onError: () => hideLoading()
                                });
                            }
                        }"
                    />
                </div>
            </div>

            <!-- Desktop: Table View -->
            <div class="hidden lg:block">
                <DataTable
                    :value="orders.data"
                    :paginator="false"
                    scrollable
                    scrollHeight="600px"
                    class="orders-table"
                >
                    <template #empty>
                        <div class="text-center py-12">
                            <div class="mb-4">
                                <i class="pi pi-inbox text-4xl text-surface-300"></i>
                            </div>
                            <h4 class="text-lg font-semibold text-surface-600 mb-2">
                                {{ hasActiveFilters() ? $t('No results with the current filters') : $t('No orders found') }}
                            </h4>
                        </div>
                    </template>
                <Column field="number" :header="$t('Number')" sortable>
                    <template #body="slotProps">
                        #{{ slotProps.data.number }}
                    </template>
                </Column>

                <Column :header="$t('Client')" style="min-width: 160px">
                    <template #body="slotProps">
                        {{ slotProps.data.client?.first_name && slotProps.data.client?.last_name ?
                         slotProps.data.client?.first_name + " " + slotProps.data.client?.last_name
                         : slotProps.data.client?.name || "-" }}
                    </template>
                </Column>

                <!-- <Column :header="$t('Invoice')">
                    <template #body="slotProps">
                        <Button
                            v-if="slotProps.data.invoice"
                            :label="`#${slotProps.data.invoice.number}`"
                            link
                            @click="() => {
                                try {
                                    const routeName = proxy.route ? proxy.route('invoices.edit', slotProps.data.invoice.id) : `/admin/invoices/${slotProps.data.invoice.id}/edit`;
                                    router.visit(routeName, {
                                        onStart: () => showLoading($t('Loading invoice...')),
                                        onFinish: () => hideLoading()
                                    });
                                } catch (e) { console.error(e); }
                            }"
                        />
                        <span v-else>-</span>
                    </template>
                </Column> -->

                <Column :header="$t('Status')" style="min-width: 200px">
                    <template #body="slotProps">
                        <Tag
                            :value="getStatusLabel(slotProps.data.status, $t)"
                            :severity="getStatusSeverity(slotProps.data.status)"
                            v-tooltip.top="getCancellationTooltip(slotProps.data, $t)"
                        />
                    </template>
                </Column>

                <Column :header="$t('Created at')" sortable field="created_at" style="min-width: 170px">
                    <template #body="slotProps">
                        {{ new Date(slotProps.data.created_at).toLocaleString() }}
                    </template>
                </Column>

                <Column :header="$t('Payment')" style="min-width: 80px">
                    <template #body="slotProps">
                        <Button
                            icon="pi pi-dollar"
                            text
                            rounded
                            :severity="slotProps.data.has_unpaid_required ? 'danger' : slotProps.data.all_paid ? 'success' : 'info'"
                            v-tooltip.top="slotProps.data.has_unpaid_required ? $t('Has items requiring payment — mark as paid before publishing') : slotProps.data.all_paid ? $t('All items paid') : $t('Has unpaid items')"
                            :disabled="slotProps.data.status === 'CANCELED'"
                            @click="openPaymentModal(slotProps.data)"
                        />
                    </template>
                </Column>

                <Column :header="$t('Actions')" headerClass="text-right" bodyClass="text-right" style="min-width: 220px">
                    <template #body="slotProps">
                        <div class="flex items-center justify-start gap-3">
                            <div class="w-28 flex-shrink-0">
                                <OrderStepProgress :order="slotProps.data" :stepIcons="slotProps.data.stepIcons || {}" />
                            </div>
                            <Button
                                icon="pi pi-eye"
                                text
                                rounded
                                v-tooltip.top="$t('Details')"
                                @click="() => openDetailsModal(slotProps.data)"
                            />
                            <Button
                                icon="pi pi-pencil"
                                text
                                rounded
                                :disabled="slotProps.data.status === 'CANCELED'"
                                @click="() => {
                                    try {
                                        const routeName = proxy.route ? proxy.route('orders.edit', slotProps.data.id) : `/admin/orders/${slotProps.data.id}/edit`;
                                        router.visit(routeName, {
                                            onStart: () => showLoading($t('Loading order...')),
                                            onFinish: () => hideLoading()
                                        });
                                    } catch (e) { console.error(e); }
                                }"
                            />
                            <span v-tooltip.top="slotProps.data.status === 'CANCELED' ? getCancellationTooltip(slotProps.data, $t) : $t('Cancel Order')">
                                <Button
                                    icon="pi pi-times-circle"
                                    text
                                    rounded
                                    :severity="slotProps.data.status === 'CANCELED' ? 'secondary' : 'danger'"
                                    :disabled="!canCancelOrder(slotProps.data)"
                                    @click="() => openCancelModal(slotProps.data)"
                                    :class="slotProps.data.status === 'CANCELED' ? 'opacity-50' : ''"
                                />
                            </span>
                        </div>
                    </template>
                </Column>

                <template #footer>
                    <Paginator
                        v-if="orders?.per_page"
                        :rows="orders.per_page"
                        :totalRecords="orders.total || 0"
                        :first="((orders.current_page || 1) - 1) * (orders.per_page || 15)"
                        @page="(event) => {
                            try {
                                const link = orders.links?.[event.page + 1];
                                if (link?.url) {
                                    router.visit(link.url, {
                                        onStart: () => showLoading($t('Loading page...')),
                                        onFinish: () => hideLoading()
                                    });
                                }
                            } catch (e) { console.error(e); }
                        }"
                    />
                    </template>
                </DataTable>
            </div>
        </div>
    </div>

    <!-- Payment Modal -->
    <Dialog
        v-model:visible="showPaymentModal"
        modal
        :header="$t('Item Payments') + (paymentModalOrder ? ` — #${paymentModalOrder.number}` : '')"
        :style="{ width: '600px' }"
        @hide="closePaymentModal"
    >
        <div v-if="paymentItems.length === 0" class="text-center py-8 text-surface-500">
            {{ $t('No items found for this order.') }}
        </div>

        <div v-else class="space-y-3">
            <div
                v-for="item in paymentItems"
                :key="item.id"
                class="flex items-center justify-between p-3 rounded-lg border"
                :class="item.paid ? 'border-green-200 bg-green-50' : 'border-surface-200 bg-surface-50'"
            >
                <div class="flex-1 min-w-0 mr-4">
                    <p class="text-sm font-medium text-surface-900 truncate">{{ item.site_url }}</p>
                    <div class="flex items-center gap-2 mt-1">
                        <span class="text-xs text-surface-500">{{ item.seller_name }}</span>
                        <Tag
                            v-if="item.seller_requires_payment"
                            :value="$t('Requires payment')"
                            severity="warn"
                            class="text-[10px] py-0 px-1"
                        />
                    </div>
                </div>
                <div class="flex items-center gap-2 flex-shrink-0">
                    <span class="text-sm" :class="item.paid ? 'text-green-600 font-medium' : 'text-surface-400'">
                        {{ item.paid ? $t('Paid') : $t('Not paid') }}
                    </span>
                    <ToggleSwitch
                        :modelValue="item.paid"
                        :disabled="togglingItemId === item.id || paymentModalOrder?.status === 'CANCELED'"
                        @update:modelValue="toggleItemPaid(item)"
                    />
                </div>
            </div>
        </div>

        <template #footer>
            <Button
                :label="$t('Close')"
                icon="pi pi-times"
                severity="secondary"
                @click="closePaymentModal"
            />
        </template>
    </Dialog>

    <!-- Cancel Order Modal -->
    <Dialog
        v-model:visible="showCancelModal"
        modal
        :header="$t('Cancel Order')"
        :style="{ width: '500px' }"
        @hide="closeCancelModal"
    >
        <div class="space-y-4">
            <p class="text-surface-700">
                {{ $t('Please provide a reason for the order cancellation. This will help the client understand why the order was canceled.') }}
            </p>

            <div class="space-y-2">
                <label for="cancelReason" class="block text-sm font-medium text-surface-700">
                    {{ $t('Cancellation Reason (Optional)') }}
                </label>
                <Textarea
                    id="cancelReason"
                    v-model="cancelReason"
                    rows="4"
                    class="w-full"
                    :placeholder="$t('Describe the reason for cancellation...')"
                />
                <small class="text-surface-600">
                    {{ $t('You can cancel the order without providing a reason, but feedback helps improve customer service.') }}
                </small>
            </div>
        </div>

        <template #footer>
            <div class="flex justify-end space-x-3">
                <Button
                    :label="$t('Cancel')"
                    icon="pi pi-times"
                    severity="secondary"
                    @click="closeCancelModal"
                />
                <Button
                    :label="$t('Confirm Cancellation')"
                    icon="pi pi-exclamation-triangle"
                    severity="danger"
                    @click="openCancelConfirmModal"
                />
            </div>
        </template>
    </Dialog>

    <!-- Order Details Modal -->
    <OrderDetailsDialog v-model:visible="showDetailsModal" :orderId="detailsOrderId" />

    <!-- Cancel Confirmation Modal -->
    <Dialog
        v-model:visible="showCancelConfirmModal"
        modal
        :header="$t('Confirm Order Cancellation')"
        :style="{ width: '400px' }"
        @hide="closeCancelConfirmModal"
    >
        <div class="space-y-4">
            <div class="flex items-start gap-3">
                <div class="w-12 h-12 rounded-full bg-red-100 flex items-center justify-center flex-shrink-0">
                    <i class="pi pi-exclamation-triangle text-red-600 text-xl"></i>
                </div>
                <div class="flex-1">
                    <h3 class="text-lg font-semibold text-surface-900 mb-2">
                        {{ $t('Are you sure?') }}
                    </h3>
                    <p class="text-surface-700">
                        {{ $t('This action cannot be undone. The order will be marked as canceled and the client will be notified.') }}
                    </p>
                </div>
            </div>
        </div>

        <template #footer>
            <div class="flex justify-end space-x-3">
                <Button
                    :label="$t('No, Keep Order')"
                    icon="pi pi-times"
                    severity="secondary"
                    @click="closeCancelConfirmModal"
                    :disabled="cancellingOrder"
                />
                <Button
                    :label="$t('Yes, Cancel Order')"
                    icon="pi pi-check"
                    severity="danger"
                    @click="confirmCancelOrder"
                    :loading="cancellingOrder"
                />
            </div>
        </template>
    </Dialog>
</template>

<style scoped>
/* Order tables: header titles must always stay on a single line */
.orders-table :deep(.p-datatable-column-title),
.add-sites-table :deep(.p-datatable-column-title) {
    white-space: nowrap;
}

.orders-table :deep(.p-datatable-header-cell),
.add-sites-table :deep(.p-datatable-header-cell) {
    white-space: nowrap;
}

.p-tag {
    font-weight: bold;
    border-radius: 0.375rem;
    padding: 0.25rem 0.5rem;
}

:deep(.p-tag.p-tag-info) {
    color: #0369a1;
}

:deep(.p-tag.p-tag-warn) {
    color: #C2410C;
}

:deep(.p-tag.p-tag-success) {
    color: #15803D;
}

:deep(.p-tag.p-tag-secondary) {
    color: #374151;
}

:deep(.p-tag.p-tag-danger) {
    color: #b91c1c;
}

:deep(.p-card-body) {
    padding: 1rem !important;
}

.custom-select :deep(.p-select) {
    height: 42px !important;
}

.custom-select :deep(.p-select-label) {
    height: 40px !important;
    line-height: 42px !important;
    display: flex !important;
    align-items: center !important;
    padding-left: 12px !important;
}

.custom-select :deep(.p-select-dropdown) {
    height: 40px !important;
    width: 42px !important;
}

.custom-select :deep(.p-select-clearable) {
    padding-right: 42px !important;
}
</style>
